import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { PERMISSIONS } from "../../utils/permissions";
import { FieldGroup, FormArray, FormBuilder } from "react-reactive-form";
import StandardPKQuestions from "../../questions/StandardPKQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router-dom";
import {
  populateValueHelperArray,
  // extractArrayValues,
} from "./utils/saveRestoreUtils";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";
import { WIZARD_SECTIONS } from "../../utils/wizardSections";
import {
  getRestorableFormArray,
  getRestoreArrayType,
  toRestorableArray,
} from "../../utils/wizardFormRestore";

const isProduction = process.env.NODE_ENV === "production";

const StandardPharmacokinetics = () => {
  const { studyId } = useParams();
  const { setAjaxLoading } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    loadWizardSections,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qPkStdColumn: "object",
    qPkTimelines: "object",
    qPkAnalytes: "object",
  };

  // const formArraysKeysToClean = ["Metabolites"];

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // Force re-render after restore so first block's qPkTimelines (and other block controls) are visible
  const [, setRestoreVersion] = useState(0);
  const lastRestoredQuestionsDataRef = useRef(null);

  useEffect(() => {
    setAjaxLoading(true);
    loadWizardSections([WIZARD_SECTIONS.standardPk])
      .then(() => {
        setErrMsg("");
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to get questions");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  }, [studyId, loadWizardSections, setAjaxLoading]);

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const wizardFormValue = questionsDataSaved.wizardForm;

      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      // Ensure block controls (including first block's qPkTimelines) exist before
      // the general form-array loop, so the first block is never missing its control.
      if (
        wizardFormValue.qPkStdAnalysis &&
        Array.isArray(wizardFormValue.qPkStdAnalysis)
      ) {
        wizardFormValue.qPkStdAnalysis.forEach((_block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";
          const qPkTimelinesControlName = `qPkTimelines${suffix}`;
          const qPkStdColumnControlName = `qPkStdColumn${suffix}`;
          if (!wizardForm.contains(qPkTimelinesControlName)) {
            wizardForm.addControl(qPkTimelinesControlName, new FormArray([]));
          }
          if (!wizardForm.contains(qPkStdColumnControlName)) {
            wizardForm.addControl(qPkStdColumnControlName, new FormArray([]));
          }
        });
      }

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (
          !getRestoreArrayType(
            wizardForm,
            key,
            formArraysKeys,
            wizardFormValue[key]
          )
        ) {
          return;
        }

        const control = getRestorableFormArray(wizardForm, key);
        while (control && control.length > 0) {
          control.removeAt(0);
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        const arrayType = getRestoreArrayType(
          wizardForm,
          key,
          formArraysKeys,
          wizardFormValue[key]
        );

        if (arrayType) {
          const control = getRestorableFormArray(wizardForm, key);
          const items = toRestorableArray(wizardFormValue[key], arrayType);

          items.forEach(item => {
            control?.push(
              arrayType === "object"
                ? FormBuilder.group(item)
                : FormBuilder.control(item)
            );
          });
        } else {
          // Ensure control exists before patching
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Ensure qPkStdEnabled control exists
      if (!wizardForm.contains("qPkStdEnabled")) {
        wizardForm.addControl(
          "qPkStdEnabled",
          FormBuilder.control(wizardFormValue.qPkStdEnabled || "false")
        );
      }

      // Ensure qPK control exists
      if (!wizardForm.contains("qPK")) {
        wizardForm.addControl(
          "qPK",
          FormBuilder.control(wizardFormValue.qPK || "false")
        );
      }

      // Handle qPkStdAnalysis - restore it to individual form controls
      if (
        wizardFormValue.qPkStdAnalysis &&
        Array.isArray(wizardFormValue.qPkStdAnalysis)
      ) {
        wizardFormValue.qPkStdAnalysis.forEach((block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";

          if (block.qPkTable !== undefined && block.qPkTable !== null) {
            const controlName = `qPkTable${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPkTable);
          }

          if (
            block.qPkPopulation !== undefined &&
            block.qPkPopulation !== null
          ) {
            const controlName = `qPkPopulation${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPkPopulation);
          }

          if (block.qPkMultiDose !== undefined && block.qPkMultiDose !== null) {
            const controlName = `qPkMultiDose${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm.get(controlName).setValue(String(block.qPkMultiDose));
          }

          if (block.qPkFigures !== undefined && block.qPkFigures !== null) {
            const controlName = `qPkFigures${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control([]));
            }
            const figuresValue = Array.isArray(block.qPkFigures)
              ? block.qPkFigures.filter(
                  item => item !== null && item !== undefined && item !== ""
                )
              : typeof block.qPkFigures === "string" && block.qPkFigures !== ""
                ? [block.qPkFigures]
                : [];
            wizardForm.get(controlName).setValue(figuresValue);
          } else {
            const controlName = `qPkFigures${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control([]));
            }
          }

          if (
            block.qPkFiguresDescription !== undefined &&
            block.qPkFiguresDescription !== null
          ) {
            const controlName = `qPkFiguresDescription${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPkFiguresDescription);
          }

          const qPkFiguresTimepointsControlName = `qPkFiguresTimepoints${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPkFiguresTimepointsControlName,
            block.qPkFiguresTimepoints
          );

          if (
            block.qPkFiguresExtra !== undefined &&
            block.qPkFiguresExtra !== null
          ) {
            const qPkFiguresExtraControlName = `qPkFiguresExtra${suffix}`;
            if (!wizardForm.contains(qPkFiguresExtraControlName)) {
              wizardForm.addControl(
                qPkFiguresExtraControlName,
                FormBuilder.control([])
              );
            }
            const figuresExtraValue = Array.isArray(block.qPkFiguresExtra)
              ? block.qPkFiguresExtra.filter(
                  item => item !== null && item !== undefined && item !== ""
                )
              : typeof block.qPkFiguresExtra === "string" &&
                  block.qPkFiguresExtra !== ""
                ? [block.qPkFiguresExtra]
                : [];
            wizardForm
              .get(qPkFiguresExtraControlName)
              .setValue(figuresExtraValue);
          } else {
            const qPkFiguresExtraControlName = `qPkFiguresExtra${suffix}`;
            if (!wizardForm.contains(qPkFiguresExtraControlName)) {
              wizardForm.addControl(
                qPkFiguresExtraControlName,
                FormBuilder.control([])
              );
            }
          }

          const qPkTimelinesControlName = `qPkTimelines${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPkTimelinesControlName,
            block.qPkTimelines
          );

          const qPkStdColumnControlName = `qPkStdColumn${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPkStdColumnControlName,
            block.qPkStdColumn
          );

          const qPkStatsAnalysisControlName = `qPkStatsAnalysis${suffix}`;
          if (!wizardForm.contains(qPkStatsAnalysisControlName)) {
            wizardForm.addControl(
              qPkStatsAnalysisControlName,
              FormBuilder.control([])
            );
          }
          if (
            block.qPkStatsAnalysis !== undefined &&
            block.qPkStatsAnalysis !== null
          ) {
            const statsValue = Array.isArray(block.qPkStatsAnalysis)
              ? block.qPkStatsAnalysis.filter(
                  item => item !== null && item !== undefined && item !== ""
                )
              : typeof block.qPkStatsAnalysis === "string" &&
                  block.qPkStatsAnalysis !== ""
                ? [block.qPkStatsAnalysis]
                : [];
            wizardForm.get(qPkStatsAnalysisControlName).setValue(statsValue);
          }
        });
      }

      setRestoreVersion(v => v + 1);
    }
  };

  // function cleanObject(obj) {
  //   for (const key in obj) {
  //     if (obj[key] === null || obj[key] === "") {
  //       return true;
  //     } else if (typeof obj[key] === "object" && cleanObject(obj[key])) {
  //       return true;
  //     }
  //   }
  //   return false;
  // }

  // Use unified submit handler that collects data from all four pages
  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    sections: [WIZARD_SECTIONS.standardPk],
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setIsSaving,
    questionsRef: null, // Standard PK page doesn't use questionsRef
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {/* <div className={"questions-content__item"}>
              <div className={"questions-content__label intro-text"}>
                <span className={"questions-content__label-text"}>
                  Please answer all of the questions.
                </span>
              </div>
            </div> */}
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <form name={"wizardForm"}>
                    <StandardPKQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      questionsData={questionsData.current}
                    />
                  </form>
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  // Determine next page based on form state
  const getNextPage = () => {
    return "/csr/" + studyId + "/comppharmacokinetics";
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Answer the following questions related to Standard Pharmacokinetics"
      }
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Safety",
        buttonGoToAction: "/csr/" + studyId + "/safety-analysis",
      }}
      nextButton={{
        buttonTitle: "Comparative PK",
        buttonGoToAction: getNextPage(),
      }}
      // nextButton={{
      //   // buttonTitle: "Data Mapping",
      //   // buttonGoToAction: "/csr/" + studyId + "/data-mapping",
      //   buttonTitle: "Standard Pharmacokinetics",
      //   buttonGoToAction: "/csr/" + studyId + "/stdpharmacokinetics",
      // }}
      saveButtonHandler={handleSubmit}
      savePermission={PERMISSIONS.csrWizardsUpdate}
      saveButton={true}
    />
  );
};

export default StandardPharmacokinetics;
